import { Component, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AcknowledgeDialogComponent } from '../acknowledge-dialog/acknowledge-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { UserService } from '../../services/user.service';

type PurgeTaskState = 'RUNNING' | 'SUCCEEDED' | 'FAILED';

interface S3PurgeStartResponse {
  taskId: string;
}

interface S3PurgeStatusResponse {
  taskId: string;
  state: PurgeTaskState;
  message?: string;
  requestedDate?: string; // YYYY-MM-DD
  startedAt?: string;
  finishedAt?: string;
  deletedByBucket?: Record<string, number>;
  scannedByBucket?: Record<string, number>;
}

@Component({
  selector: 'app-s3-data-purge-admin',
  templateUrl: './s3-data-purge-admin.component.html',
  styleUrls: ['./s3-data-purge-admin.component.scss']
})
export class S3DataPurgeAdminComponent implements OnDestroy {
  // YYYY-MM-DD strings (UTC-based)
  purgeBeforeDate: string;
  maxAllowedDate: string;

  loading = false;
  running = false;
  taskId: string | null = null;
  status: S3PurgeStatusResponse | null = null;

  private pollTimer: any = null;

  constructor(private dialog: MatDialog, private userService: UserService) {
    const utcTodayMs = Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());
    const maxMs = utcTodayMs - 7 * 24 * 60 * 60 * 1000; // keep last 7 days
    this.maxAllowedDate = new Date(maxMs).toISOString().substring(0, 10);

    const d = new Date(utcTodayMs);
    d.setUTCMonth(d.getUTCMonth() - 3);
    this.purgeBeforeDate = d.toISOString().substring(0, 10);
    // If default ends up > max (unlikely), clamp to max for safety
    if (this.purgeBeforeDate > this.maxAllowedDate) {
      this.purgeBeforeDate = this.maxAllowedDate;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get isDateValid(): boolean {
    if (!this.purgeBeforeDate) return false;
    // Must keep last 7 days => selected date must be <= maxAllowedDate
    return this.purgeBeforeDate <= this.maxAllowedDate;
  }

  onPurgeClick(): void {
    if (!this.isDateValid) {
      this.dialog.open(AcknowledgeDialogComponent, { data: `Invalid date. Please select a date on or before ${this.maxAllowedDate} (UTC).` });
      return;
    }
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '460px',
      data: {
        title: 'Confirm S3 Purge',
        message: `This will permanently delete S3 data under users/ older than ${this.purgeBeforeDate} (UTC). Continue?`
      }
    });
    ref.afterClosed().subscribe(ok => {
      if (!ok) return;
      this.startPurge();
    });
  }

  private startPurge(): void {
    this.loading = true;
    this.status = null;
    this.taskId = null;
    this.userService.startS3DataPurge(this.purgeBeforeDate).subscribe({
      next: (res: S3PurgeStartResponse) => {
        this.loading = false;
        this.taskId = res?.taskId || null;
        if (!this.taskId) {
          this.dialog.open(AcknowledgeDialogComponent, { data: 'Purge started but taskId is missing in response.' });
          return;
        }
        this.running = true;
        this.startPolling();
      },
      error: (err) => {
        this.loading = false;
        this.dialog.open(AcknowledgeDialogComponent, { data: `Purge request failed: ${err?.error?.error || err?.error?.message || err.message}` });
      }
    });
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollOnce();
    this.pollTimer = setInterval(() => this.pollOnce(), 2000);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private pollOnce(): void {
    if (!this.taskId) return;
    this.userService.getS3DataPurgeStatus(this.taskId).subscribe({
      next: (st: S3PurgeStatusResponse) => {
        this.status = st;
        const state = st?.state;
        if (state === 'SUCCEEDED' || state === 'FAILED') {
          this.running = false;
          this.stopPolling();
        }
      },
      error: (err) => {
        // stop polling on hard errors (e.g., task not found)
        this.running = false;
        this.stopPolling();
        this.dialog.open(AcknowledgeDialogComponent, { data: `Failed to fetch purge status: ${err?.error?.error || err?.error?.message || err.message}` });
      }
    });
  }

  get bucketRows(): Array<{ bucket: string; deleted: number; scanned: number }> {
    const del = this.status?.deletedByBucket || {};
    const scan = this.status?.scannedByBucket || {};
    const keys = Array.from(new Set([...Object.keys(del), ...Object.keys(scan)])).sort();
    return keys.map(k => ({ bucket: k, deleted: del[k] ?? 0, scanned: scan[k] ?? 0 }));
  }
}


